import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const getConditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-green-100 text-green-700';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-red-100 text-red-700';
  if (condition === 'Unknown') return 'bg-slate-100 text-slate-500';
  return 'bg-yellow-100 text-yellow-700';
};

const getPriceSourceBadgeClass = (priceSource) => {
  if (priceSource === 'ebay_rag') return 'bg-blue-100 text-blue-700';
  if (priceSource === 'image') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

const getPriceSourceLabel = (priceSource) => {
  if (priceSource === 'ebay_rag') return 'eBay RAG';
  if (priceSource === 'image') return 'Image';
  return 'Model Knowledge';
};

const AnalysisItemsList = ({
  items,
  currentImageIndex,
  originalPrices,
  originalNames,
  updatingId,
  updatingNameId,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
}) => {
  return (
    <div className="w-full md:w-1/2 space-y-3 sm:space-y-4">
      {(items || []).map((item, idx) => {
        const isDirty = item.id
          && originalPrices[item.id] !== undefined
          && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
        const isUpdating = updatingId === item.id;
        const isNameDirty = item.id
          && originalNames[item.id] !== undefined
          && originalNames[item.id] !== (item.manual_name || '');
        const isNameUpdating = updatingNameId === item.id;
        const condition = item?.condition || 'Unknown';
        const priceSource = item?.price_source || 'model_knowledge';

        return (
          <div key={idx} className="bg-white p-3 sm:p-4 rounded border border-slate-200 shadow-sm hover:border-blue-300 transition-colors">
            <strong className="block text-slate-700 text-base sm:text-lg mb-1">
              {item.bounding_boxes
                && Array.isArray(item.bounding_boxes)
                && item.bounding_boxes[currentImageIndex]
                && item.bounding_boxes[currentImageIndex].label
                ? item.bounding_boxes[currentImageIndex].label
                : item.item}
            </strong>

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
              <span className="text-slate-500 text-xs sm:text-sm">Manual Name:</span>
              <div className="relative flex items-center flex-1">
                <input
                  type="text"
                  placeholder="Enter custom name..."
                  className={`w-full pr-8 py-1 px-2 border rounded text-sm text-slate-700 focus:outline-none focus:ring-1 transition-all ${
                    isNameDirty
                      ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30'
                      : 'border-slate-200 focus:ring-green-500'
                  }`}
                  value={item.manual_name || ''}
                  onChange={(e) => onManualNameChange(item.id, e.target.value)}
                />
                {item.id && (
                  <button
                    onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                    disabled={!isNameDirty || isNameUpdating}
                    className={`absolute right-1 p-1 rounded transition-all ${
                      isNameUpdating
                        ? 'text-slate-400'
                        : isNameDirty
                          ? 'text-blue-600 hover:bg-blue-100'
                          : 'text-green-500 cursor-default'
                    }`}
                    title={isNameDirty ? 'Save Name' : 'Name Saved'}
                  >
                    {isNameUpdating ? (
                      <RefreshCcw size={14} className="animate-spin" />
                    ) : isNameDirty ? (
                      <Save size={14} />
                    ) : (
                      <Check size={14} />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              <p className="text-slate-600 text-sm sm:text-base">Estimated Value: <span className="font-bold text-green-600">${item.estimated_value}</span></p>
              <PriceDistributionView
                rangeValue={item?.estimated_value_range}
                distributionValue={item?.price_estimate_distribution}
              />
              <p className="text-slate-600 text-sm sm:text-base">Confidence: <span className="font-bold text-indigo-600">{formatConfidencePercent(item?.confidence_score)}</span></p>
              <div className="flex items-center space-x-2">
                <span className="text-slate-600 text-xs sm:text-sm">Actual Price:</span>
                <div className="relative flex items-center">
                  <span className="absolute left-2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                      isDirty
                        ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30'
                        : 'border-slate-200 focus:ring-green-500'
                    }`}
                    value={item.actual_price}
                    onChange={(e) => onPriceChange(item.id, e.target.value)}
                    step="0.01"
                  />
                  {item.id && (
                    <button
                      onClick={() => onUpdatePrice(item.id, item.actual_price)}
                      disabled={!isDirty || isUpdating}
                      className={`absolute right-1 p-1 rounded transition-all ${
                        isUpdating
                          ? 'text-slate-400'
                          : isDirty
                            ? 'text-blue-600 hover:bg-blue-100'
                            : 'text-green-500 cursor-default'
                      }`}
                      title={isDirty ? 'Save Price' : 'Price Saved'}
                    >
                      {isUpdating ? (
                        <RefreshCcw size={14} className="animate-spin" />
                      ) : isDirty ? (
                        <Save size={14} />
                      ) : (
                        <Check size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2">
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-slate-400">Condition:</span>
                <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${getConditionBadgeClass(condition)}`}>
                  {condition}
                </span>
                <span className="text-[9px] text-slate-400 ml-1">AI Src:</span>
                <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${getPriceSourceBadgeClass(priceSource)}`}>
                  {getPriceSourceLabel(priceSource)}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {(items || []).length >= 1 && (
        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg flex justify-between items-center">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
          <span className="text-base text-green-600 font-bold">
            ${(items || []).reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

export default AnalysisItemsList;
